/* The browser mirrors backend/src/pricing/derive.ts. New project decisions
   use one development duration/team, QA calendar time and one client price.
   Historical per-case sheets preserve their existing calendar and cost.
   Both surfaces preview the same math that produces the client document. */

import {
  EngineerLevel, PlanMode, PricingMonthlyRow, PricingPlan, PricingRules, PricingSetupRow, UsageUnit, PricingSheet, PricingSection, PricingCasePlan, PricingCaseSection,
} from './api';

import { simpleMonthlyLines } from './monthlySimpleMath';
import { monthlyCaseScopeBasis } from './monthlyCaseScope';

export const DEFAULT_RULES: PricingRules = {
  week_days: 5,
  middle_week_rate: 800,
  senior_week_rate: 1250,
  unit_rates: { call: 1, sms: 3, email: 1, sms_email: 4 },
  unit_minimums: { call: 500, sms: 100, email: 100, sms_email: 100, custom: 100 },
};

export const UNIT_OPTIONS: Array<{ value: UsageUnit; label: string; hint: string }> = [
  { value: 'call', label: 'Calls', hint: '$ per 2-minute call' },
  { value: 'sms', label: 'SMS', hint: '$ per unique contact / month, unlimited texts' },
  { value: 'email', label: 'Email', hint: '$ per unique contact / month, unlimited emails' },
  { value: 'sms_email', label: 'SMS + Email', hint: '$ per contact / month, both channels' },
  { value: 'custom', label: 'Custom', hint: 'your own unit — «10 leads», «document», «page»' },
];

export const UNIT_WORDS: Record<UsageUnit, { one: string; many: string; note: string }> = {
  call: { one: 'call', many: 'calls', note: 'a 2-minute call' },
  sms: { one: 'contact', many: 'contacts', note: 'unlimited SMS to each contact' },
  email: { one: 'contact', many: 'contacts', note: 'unlimited email to each contact' },
  sms_email: { one: 'contact', many: 'contacts', note: 'unlimited SMS + email to each contact' },
  custom: { one: 'unit', many: 'units', note: '' },
};

export const round2 = (v: number) => Math.round(v * 100) / 100;

export function usd(v: number): string {
  return '$' + round2(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** «$48,000.00» / «48000» / «$1.2k» → number, or null when it is not one. */
export function parseMoney(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = String(s).replace(/,/g, '').match(/(-?\d+(?:\.\d+)?)(?:\s*([km])\b)?/i);
  if (!m) return null;
  const multiplier = m[2]?.toLowerCase() === 'k' ? 1000 : m[2]?.toLowerCase() === 'm' ? 1000000 : 1;
  const n = parseFloat(m[1]) * multiplier;
  return Number.isFinite(n) ? n : null;
}

export function weeksFromDays(days: number, weekDays = 5): number {
  if (!Number.isFinite(days) || days <= 0) return 0;
  return round2(days / (weekDays > 0 ? weekDays : 5));
}
export function weeksStr(w: number): string {
  return Number.isFinite(w) && w > 0 ? String(round2(w)) : '';
}
export function parseWeeks(s: string | number | null | undefined): number {
  if (typeof s === 'number') return Number.isFinite(s) ? s : 0;
  const n = parseFloat(String(s ?? '').replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export interface Schedule {
  stages: Array<{ stage: number; cases: string[]; weeks: number }>;
  qa_stages?: QASchedule['stages'];
  dev_weeks: number;
  qa_weeks: number;
  total_weeks: number;
  person_weeks: number;
}

export interface QASchedule {
  stages: Array<{ stage: number; cases: string[]; weeks: number | null }>;
  /** Last agreed project allowance while a per-case draft is incomplete. */
  weeks: number;
  complete: boolean;
}

/** QA is a separate calendar after development. Each group runs in parallel;
 * its longest case determines the group duration. A missing value is never
 * interpreted as zero, and incomplete drafts retain the global allowance. */
export function computeQASchedule(setup: PricingSetupRow[], plan?: PricingPlan | null): QASchedule {
  const fallback = typeof plan?.qa_weeks === 'number' && Number.isFinite(plan.qa_weeks) && plan.qa_weeks >= 0
    ? round2(plan.qa_weeks) : 0;
  if (plan?.qa_cases === undefined) return { stages: [], weeks: fallback,
    complete: typeof plan?.qa_weeks === 'number' && Number.isFinite(plan.qa_weeks)
      && plan.qa_weeks >= 0 && plan.qa_weeks <= 520 };
  const names = setup.map(row => row.case);
  const rows = plan.qa_cases;
  const accepted = new Set(names);
  const incomplete = { stages: [], weeks: fallback, complete: false };
  if (!Array.isArray(rows) || !rows.length || rows.length > 40
      || rows.length !== names.length || accepted.size !== names.length || names.some(name => !name)
      || (plan.qa_mode !== undefined && !['sequential', 'parallel', 'custom'].includes(plan.qa_mode))) return incomplete;
  const seen = new Set<string>();
  const byStage = new Map<number, { cases: string[]; weeks: number | null }>();
  for (const row of rows) {
    if (!row || !accepted.has(row.case) || seen.has(row.case)
        || !Number.isInteger(row.stage) || row.stage < 1 || row.stage > 40
        || (row.weeks !== null && (typeof row.weeks !== 'number' || !Number.isFinite(row.weeks)
          || row.weeks < 0 || row.weeks > 52))) return incomplete;
    seen.add(row.case);
    const current = byStage.get(row.stage) ?? { cases: [], weeks: 0 };
    current.cases.push(row.case);
    current.weeks = current.weeks === null || row.weeks === null ? null : Math.max(current.weeks, round2(row.weeks));
    byStage.set(row.stage, current);
  }
  const stages = Array.from(byStage.entries()).sort((a, b) => a[0] - b[0])
    .map(([stage, group]) => ({ stage, ...group }));
  const total = round2(stages.reduce((sum, group) => sum + (group.weeks ?? 0), 0));
  const complete = stages.every(group => group.weeks !== null) && total <= 520;
  return { stages, weeks: complete ? total : fallback, complete };
}

export function computeSchedule(setup: PricingSetupRow[], plan?: PricingPlan | null): Schedule {
  const byCase = casePricingEnabled(plan) ? new Map(plan!.case_pricing!.map(value => [value.case, value])) : null;
  const byStage = new Map<number, { cases: string[]; weeks: number }>();
  let personWeeks = 0;
  setup.forEach((r, i) => {
    const decision = byCase?.get(r.case);
    const w = decision && finiteAmount(decision.dev_weeks) ? round2(decision.dev_weeks) : parseWeeks(r.weeks);
    const st = byCase && plan?.mode === 'parallel' ? 1 : byCase && plan?.mode === 'sequential' ? i + 1
      : Number.isInteger(r.stage) && (r.stage as number) >= 1 ? (r.stage as number) : i + 1;
    const cur = byStage.get(st) ?? { cases: [], weeks: 0 };
    cur.cases.push(r.case);
    cur.weeks = Math.max(cur.weeks, w);
    byStage.set(st, cur);
    personWeeks += w * Math.max(1, decision?.dev_engineers ?? r.engineers ?? 1);
  });
  const stages = Array.from(byStage.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([stage, v]) => ({ stage, cases: v.cases, weeks: round2(v.weeks) }));
  const sourceDev = round2(stages.reduce((a, s) => a + s.weeks, 0));
  const dev = !byCase && plan?.workflow_version === 'lato-pricing-v2' && finiteAmount(plan.dev_weeks)
    ? round2(plan.dev_weeks) : sourceDev;
  const qa = computeQASchedule(setup, plan);
  return { stages, ...(Array.isArray(plan?.qa_cases) ? { qa_stages: qa.stages } : {}),
    dev_weeks: dev, qa_weeks: qa.weeks, total_weeks: round2(dev + qa.weeks), person_weeks: round2(personWeeks) };
}

export function planModeOf(setup: PricingSetupRow[]): PlanMode {
  const stages = setup.map((r, i) => (Number.isInteger(r.stage) && (r.stage as number) >= 1 ? (r.stage as number) : i + 1));
  if (stages.every((s) => s === 1)) return setup.length > 1 ? 'parallel' : 'sequential';
  if (stages.every((s, i) => s === i + 1)) return 'sequential';
  return 'custom';
}

/** Apply a toggle: every case its own stage, or all in stage 1. */
export function applyMode(setup: PricingSetupRow[], mode: 'sequential' | 'parallel'): PricingSetupRow[] {
  return setup.map((r, i) => ({ ...r, stage: mode === 'parallel' ? 1 : i + 1 }));
}

export function rateFor(level: EngineerLevel | undefined, rules: PricingRules): number {
  return level === 'senior' ? rules.senior_week_rate : rules.middle_week_rate;
}

export interface CostBreakdown {
  cases: Array<{ case: string; weeks: number; engineers: number; level: EngineerLevel; cost: number }>;
  qa_cost: number;
  total: number;
  calculated_total: number;
  is_override: boolean;
}

export function computeCost(setup: PricingSetupRow[], plan: PricingPlan | null | undefined, rules: PricingRules): CostBreakdown {
  if (casePricingEnabled(plan)) {
    const decisions = plan!.case_pricing!;
    const costs = decisions.map(value => computeCaseCost(value, rules));
    const cases = setup.map(row => {
      const decision = decisions.find(value => value.case === row.case);
      return { case: row.case, weeks: decision?.dev_weeks ?? parseWeeks(row.weeks),
        engineers: decision?.dev_engineers ?? 1, level: decision?.dev_level ?? 'middle' as EngineerLevel,
        cost: decision ? computeCaseCost(decision, rules).total : 0 };
    });
    return { cases, qa_cost: 0, total: round2(costs.reduce((sum, value) => sum + value.total, 0)),
      calculated_total: round2(costs.reduce((sum, value) => sum + value.calculated_total, 0)),
      is_override: costs.some(value => value.is_override) };
  }
  if (plan?.workflow_version === 'lato-pricing-v2') {
    const weeks = finiteAmount(plan.dev_weeks) ? round2(plan.dev_weeks) : 0;
    const engineers = Math.max(1, plan.dev_engineers ?? 1);
    const level: EngineerLevel = plan.dev_level === 'senior' ? 'senior' : 'middle';
    const rate = finiteAmount(plan.dev_week_rate) ? plan.dev_week_rate : rateFor(level, rules);
    const calculated = round2(weeks * engineers * rate);
    const agreed = finiteAmount(plan.internal_cost) ? round2(plan.internal_cost) : calculated;
    const allocated = allocateSetupPrice(setup, calculated);
    const cases = setup.map((row, i) => ({ case: row.case, weeks: parseWeeks(row.weeks),
      engineers, level, cost: parseMoney(allocated[i]?.price) ?? 0 }));
    return { cases, qa_cost: 0, total: agreed, calculated_total: calculated,
      is_override: finiteAmount(plan.internal_cost) && agreed !== calculated };
  }
  const cases = setup.map((r) => {
    const weeks = parseWeeks(r.weeks);
    const engineers = Math.max(1, r.engineers ?? 1);
    const level: EngineerLevel = r.level === 'senior' ? 'senior' : 'middle';
    return { case: r.case, weeks, engineers, level, cost: round2(weeks * engineers * rateFor(level, rules)) };
  });
  const qa = Array.isArray(plan?.qa_cases) ? computeQASchedule(setup, plan) : null;
  const qaWeeks = qa?.complete ? qa.weeks : plan && plan.qa_weeks > 0 ? plan.qa_weeks : 0;
  const qaCost = round2(qaWeeks * Math.max(1, plan?.qa_engineers ?? 1) * rateFor(plan?.qa_level, rules));
  const total = round2(cases.reduce((a, c) => a + c.cost, 0) + qaCost);
  return { cases, qa_cost: qaCost, total, calculated_total: total, is_override: false };
}


/** Explicitly opt a historical per-case sheet into the project decisions.
 * Existing calendar, QA, team and client amounts are used as the starting
 * point; opening a saved sheet never activates a new pricing policy. */
export function createProjectPricingPlan(sheet: PricingSheet, rules: PricingRules = DEFAULT_RULES): PricingPlan {
  if (sheet.plan?.workflow_version === 'lato-pricing-v2') return normalizeProjectPlan(sheet.plan);
  const schedule = computeSchedule(sheet.setup, sheet.plan);
  const first = sheet.setup[0];
  const level: EngineerLevel = first?.level === 'senior' ? 'senior' : 'middle';
  const numericPrices = sheet.setup.map(row => parseMoney(row.price));
  return {
    ...(sheet.plan ?? { mode: planModeOf(sheet.setup), qa_weeks: 0 }),
    workflow_version: 'lato-pricing-v2',
    dev_weeks: schedule.dev_weeks,
    dev_engineers: Math.max(1, first?.engineers ?? 1),
    dev_level: level,
    dev_week_rate: rateFor(level, rules),
    internal_cost: null,
    sale_price: numericPrices.some(value => value !== null)
      ? round2(numericPrices.reduce<number>((sum, value) => sum + (value ?? 0), 0)) : null,
    section_confirmations: {},
  };
}

/** A transparent recommendation; no rounding to an arbitrary quote tier. */
export function recommendedSalePrice(internalCost: number, multiplier = 2.2): number {
  return Number.isFinite(internalCost) && internalCost >= 0 && Number.isFinite(multiplier) && multiplier >= 0
    ? round2(internalCost * multiplier) : 0;
}

/** Largest remainder allocation in cents. Every source case stays present;
 * the document's case fees add to the one amount the author agreed. */
export function allocateSetupPrice(setup: PricingSetupRow[], salePrice: number): PricingSetupRow[] {
  if (!setup.length || !Number.isFinite(salePrice) || salePrice < 0) return setup;
  let weights = setup.map(row => Math.max(0, parseWeeks(row.weeks)));
  if (!weights.some(weight => weight > 0)) weights = setup.map(() => 1);
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  const cents = Math.round(salePrice * 100);
  const exact = weights.map(weight => cents * weight / weightTotal);
  const allocated = exact.map(value => Math.floor(value));
  let remainder = cents - allocated.reduce((sum, value) => sum + value, 0);
  const order = exact.map((value, index) => ({ index, fraction: value - allocated[index] }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (const entry of order) {
    if (remainder-- <= 0) break;
    allocated[entry.index] += 1;
  }
  return setup.map((row, index) => ({ ...row, price: usd(allocated[index] / 100) }));
}

export function pricingCaseWeeks(setup: PricingSetupRow[], plan?: PricingPlan | null): Array<{ case: string; weeks: string }> {
  if (casePricingEnabled(plan)) return setup.filter(row => row.case).map(row => ({ case: row.case,
    weeks: String(plan!.case_pricing!.find(value => value.case === row.case)?.dev_weeks ?? parseWeeks(row.weeks)) }));
  if (plan?.workflow_version !== 'lato-pricing-v2' || !finiteAmount(plan.dev_weeks)) {
    return setup.filter(row => row.case && row.weeks).map(row => ({ case: row.case, weeks: row.weeks }));
  }
  const source = computeSchedule(setup);
  const stages = allocateSetupPrice(source.stages.map(stage => ({
    case: String(stage.stage), weeks: String(stage.weeks), price: null,
  })), plan.dev_weeks);
  const agreed = new Map(stages.map(stage => [Number(stage.case), parseMoney(stage.price) ?? 0]));
  return setup.filter(row => row.case).map((row, index) => {
    const stage = Number.isInteger(row.stage) && (row.stage ?? 0) >= 1 ? row.stage! : index + 1;
    const original = source.stages.find(value => value.stage === stage)?.weeks ?? 0;
    const duration = agreed.get(stage) ?? 0;
    const weeks = original > 0 ? round2(parseWeeks(row.weeks) / original * duration) : duration;
    return { case: row.case, weeks: String(weeks) };
  });
}


export function casePricingEnabled(plan?: PricingPlan | null): boolean {
  return plan?.workflow_version === 'lato-pricing-v2' && Array.isArray(plan.case_pricing);
}

export function getCasePricing(sheet: PricingSheet, caseName: string): PricingCasePlan | undefined {
  return casePricingEnabled(sheet.plan) ? sheet.plan!.case_pricing!.find(value => value.case === caseName) : undefined;
}

export function computeCaseCost(decision: PricingCasePlan, rules: PricingRules = DEFAULT_RULES): {
  calculated_total: number; total: number; is_override: boolean;
} {
  const weeks = finiteAmount(decision.dev_weeks) ? round2(decision.dev_weeks) : 0;
  const engineers = Math.max(1, decision.dev_engineers ?? 1);
  const rate = finiteAmount(decision.dev_week_rate) ? decision.dev_week_rate : rateFor(decision.dev_level, rules);
  const calculated = round2(weeks * engineers * rate);
  const total = finiteAmount(decision.internal_cost) ? round2(decision.internal_cost) : calculated;
  return { calculated_total: calculated, total, is_override: total !== calculated };
}

/** A missing case amount remains undecided; it is never inferred as zero. */
export function caseSaleTotal(plan?: PricingPlan | null): number | null {
  if (!casePricingEnabled(plan) || !plan!.case_pricing!.length
      || plan!.case_pricing!.some(value => !finiteAmount(value.sale_price))) return null;
  return round2(plan!.case_pricing!.reduce((sum, value) => sum + round2(value.sale_price!), 0));
}

/** Explicit migration preserves agreed legacy/global calendar, costs and fees. */
export function createCasePricingPlan(sheet: PricingSheet, rules: PricingRules = DEFAULT_RULES): PricingPlan {
  if (casePricingEnabled(sheet.plan)) return normalizeProjectPlan(sheet.plan!);
  const global = createProjectPricingPlan(sheet, rules);
  const wasGlobal = sheet.plan?.workflow_version === 'lato-pricing-v2';
  // The selected mode becomes authoritative in case review. Rebase the
  // calendar before splitting a prior global override so a stale legacy
  // stage layout cannot shorten its agreed delivery date during opt-in.
  const selectedStages = sheet.setup.map((row, index) => ({ ...row,
    stage: global.mode === 'parallel' ? 1 : global.mode === 'sequential' ? index + 1 : row.stage }));
  const sourceWeeks = wasGlobal ? pricingCaseWeeks(selectedStages, sheet.plan) : sheet.setup.map(row => ({ case: row.case, weeks: row.weeks }));
  const globalCost = wasGlobal ? computeCost(sheet.setup, sheet.plan, rules).total : null;
  const agreedCosts = globalCost !== null ? allocateSetupPrice(sheet.setup, globalCost) : null;
  const globalPrices = finiteAmount(global.sale_price) ? allocateSetupPrice(sheet.setup, global.sale_price) : null;
  const numericPrices = sheet.setup.map(row => parseMoney(row.price));
  const pricesMatch = finiteAmount(global.sale_price) && numericPrices.every(value => value !== null)
    && round2(numericPrices.reduce<number>((sum, value) => sum + (value ?? 0), 0)) === global.sale_price;
  const decisions: PricingCasePlan[] = sheet.setup.map((row, index) => {
    const level = wasGlobal ? global.dev_level! : row.level === 'senior' ? 'senior' : 'middle';
    const decision: PricingCasePlan = { case: row.case,
      dev_weeks: parseWeeks(sourceWeeks[index]?.weeks),
      dev_engineers: Math.max(1, wasGlobal ? global.dev_engineers ?? 1 : row.engineers ?? 1),
      dev_level: level, dev_week_rate: wasGlobal ? global.dev_week_rate ?? rateFor(level, rules) : rateFor(level, rules),
      internal_cost: null,
      sale_price: pricesMatch ? numericPrices[index] : globalPrices ? parseMoney(globalPrices[index]?.price) : numericPrices[index],
      section_confirmations: {} };
    const preservedCost = agreedCosts ? parseMoney(agreedCosts[index]?.price) : null;
    if (preservedCost !== null && preservedCost !== computeCaseCost(decision, rules).total) decision.internal_cost = preservedCost;
    return decision;
  });
  const plan = { ...global, case_pricing: decisions, section_confirmations: {} };
  return { ...plan, dev_weeks: computeSchedule(sheet.setup, plan).dev_weeks,
    internal_cost: null, sale_price: caseSaleTotal(plan) };
}

function casePricingSectionBasis(sheet: PricingSheet, caseName: string, section: PricingCaseSection): string {
  const row = sheet.setup.find(value => value.case === caseName);
  const decision = getCasePricing(sheet, caseName);
  const development = [caseName, row?.days ?? null, row?.weeks ?? null, decision?.dev_weeks ?? null];
  return JSON.stringify(section === 'development' ? development : [development, decision?.dev_engineers ?? null,
    decision?.dev_level ?? null, decision?.dev_week_rate ?? null, decision?.internal_cost ?? null, decision?.sale_price ?? null]);
}

export function casePricingSectionConfirmed(sheet: PricingSheet, caseName: string, section: PricingCaseSection): boolean {
  const decision = getCasePricing(sheet, caseName);
  return !!decision && decision.section_confirmations?.[section] === casePricingSectionBasis(sheet, caseName, section);
}

export function invalidateCasePricingSections(sheet: PricingSheet, caseName: string, section: PricingCaseSection): PricingSheet {
  if (!casePricingEnabled(sheet.plan)) return sheet;
  const case_pricing = sheet.plan!.case_pricing!.map(decision => {
    if (decision.case !== caseName) return decision;
    const section_confirmations = { ...decision.section_confirmations };
    if (section === 'development') delete section_confirmations.development;
    delete section_confirmations.setup;
    return { ...decision, section_confirmations };
  });
  return invalidatePricingSections({ ...sheet, plan: { ...sheet.plan!, case_pricing } }, section);
}

export function confirmCasePricingSection(sheet: PricingSheet, caseName: string, section: PricingCaseSection,
  rules: PricingRules = DEFAULT_RULES): PricingSheet {
  const plan = createCasePricingPlan(sheet, rules);
  let next = invalidateCasePricingSections({ ...sheet, plan }, caseName, section);
  if (section === 'setup' && !casePricingSectionConfirmed(next, caseName, 'development')) return next;
  next = { ...next, plan: { ...next.plan!, case_pricing: next.plan!.case_pricing!.map(decision => decision.case === caseName
    ? { ...decision, dev_week_rate: finiteAmount(decision.dev_week_rate) ? decision.dev_week_rate : rateFor(decision.dev_level, rules) }
    : decision) } };
  return { ...next, plan: { ...next.plan!, case_pricing: next.plan!.case_pricing!.map(decision => decision.case === caseName
    ? { ...decision, section_confirmations: { ...decision.section_confirmations,
      [section]: casePricingSectionBasis(next, caseName, section) } } : decision) } };
}

const PRICING_SECTIONS: PricingSection[] = ['development', 'qa', 'setup', 'monthly'];
const finiteAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

/** Project decisions use the same two-decimal weeks and money displayed
 * in the workspace. Avoid a hidden extra fraction changing client math. */
export function normalizeProjectPlan(plan: PricingPlan): PricingPlan {
  if (Array.isArray(plan.qa_cases)) plan = { ...plan, qa_cases: plan.qa_cases.map(row => ({ ...row,
    ...(finiteAmount(row.weeks) ? { weeks: round2(row.weeks) } : {}),
  })) };
  if (plan.workflow_version !== 'lato-pricing-v2') return plan;
  return { ...plan,
    ...(finiteAmount(plan.dev_weeks) ? { dev_weeks: round2(plan.dev_weeks) } : {}),
    ...(finiteAmount(plan.qa_weeks) ? { qa_weeks: round2(plan.qa_weeks) } : {}),
    ...(finiteAmount(plan.internal_cost) ? { internal_cost: round2(plan.internal_cost) } : {}),
    ...(casePricingEnabled(plan) ? { case_pricing: plan.case_pricing!.map(decision => ({ ...decision,
      ...(finiteAmount(decision.dev_weeks) ? { dev_weeks: round2(decision.dev_weeks) } : {}),
      ...(finiteAmount(decision.internal_cost) ? { internal_cost: round2(decision.internal_cost) } : {}),
      ...(finiteAmount(decision.sale_price) ? { sale_price: round2(decision.sale_price) } : {}),
    })), sale_price: caseSaleTotal(plan) }
      : finiteAmount(plan.sale_price) ? { sale_price: round2(plan.sale_price) } : {}),
  };
}

/** Exact canonical snapshots avoid stale local checks after a duration,
 * source case, team, sale amount or monthly contract changes. These are UI
 * review state, not credentials; the server still authorizes every decision. */
function pricingSectionBasis(sheet: PricingSheet, section: PricingSection): string {
  const plan = sheet.plan;
  const development = [casePricingEnabled(plan)
    ? plan!.case_pricing!.map(value => [value.case, value.dev_weeks]) : plan?.dev_weeks ?? null, plan?.mode ?? null,
    sheet.setup.map(row => [row.case.trim(), row.days ?? null, row.weeks.trim(), row.stage ?? null])];
  const qa: unknown[] = [development, plan?.qa_weeks ?? null];
  if (Array.isArray(plan?.qa_cases)) qa.push(['qa-cases-v1', plan?.qa_mode ?? null,
    plan!.qa_cases!.map(row => [row.case, row.weeks ?? null, row.stage])]);
  const setup = casePricingEnabled(plan) ? [qa, plan!.case_pricing!.map(value => [value.case,
    value.dev_engineers, value.dev_level, value.dev_week_rate ?? null, value.internal_cost ?? null, value.sale_price ?? null])]
    : [qa, plan?.dev_engineers ?? null, plan?.dev_level ?? null,
      plan?.dev_week_rate ?? null, plan?.internal_cost ?? null, plan?.sale_price ?? null];
  if (section === 'development') return JSON.stringify(development);
  if (section === 'qa') return JSON.stringify(qa);
  if (section === 'setup') return JSON.stringify(setup);
  const monthly = sheet.monthly.map(row => {
    const structured = row.model === 'fixed' || row.model === 'usage' || row.model === 'simple';
    const basis: unknown[] = [row.model ?? null, row.model === 'simple' ? row.simple?.bucket : row.label.trim(),
      structured ? null : row.price ?? null, structured ? null : row.overage ?? null,
      row.unit ?? null, row.unit_label?.trim() || null, row.rate ?? null,
      row.minimum ?? null, row.amount ?? null,
      row.model === 'simple' ? row.simple?.charges.map(charge => {
        const chargeBasis: unknown[] = [charge.ruleId, charge.quantity ?? null,
          charge.minimum ?? null, charge.manualRate ?? null, charge.unitCost ?? null];
        const scope = monthlyCaseScopeBasis(charge.caseNames);
        if (scope) chargeBasis.push(scope);
        return chargeBasis;
      }) : null];
    if (row.model === 'simple' && (row.displayLabel?.trim() || row.displayDescription?.trim())) basis.push([row.displayLabel?.trim() || null, row.displayDescription?.trim() || null]);
    const scope = monthlyCaseScopeBasis(row.caseNames);
    if (scope) basis.push(scope);
    return basis;
  });
  return JSON.stringify([setup, monthly]);
}

export function pricingSectionConfirmed(sheet: PricingSheet, section: PricingSection): boolean {
  if (section !== 'development' && sheet.plan?.qa_cases !== undefined
      && !computeQASchedule(sheet.setup, sheet.plan).complete) return false;
  return sheet.plan?.workflow_version === 'lato-pricing-v2'
    && sheet.plan.section_confirmations?.[section] === pricingSectionBasis(sheet, section);
}

export function invalidatePricingSections(sheet: PricingSheet, section: PricingSection): PricingSheet {
  if (sheet.plan?.workflow_version !== 'lato-pricing-v2') return sheet;
  const confirmations = { ...sheet.plan.section_confirmations };
  PRICING_SECTIONS.slice(PRICING_SECTIONS.indexOf(section)).forEach(key => delete confirmations[key]);
  return { ...sheet, plan: { ...sheet.plan, section_confirmations: confirmations } };
}

export function confirmPricingSection(sheet: PricingSheet, section: PricingSection, rules: PricingRules = DEFAULT_RULES): PricingSheet {
  const plan = createProjectPricingPlan(sheet, rules);
  if (plan.qa_cases !== undefined) {
    const qa = computeQASchedule(sheet.setup, plan);
    if (section !== 'development' && !qa.complete) return sheet;
    if (qa.complete) plan.qa_weeks = qa.weeks;
  }
  if (casePricingEnabled(plan) && (section === 'development' || section === 'setup')
      && plan.case_pricing!.some(value => !casePricingSectionConfirmed({ ...sheet, plan }, value.case, section))) return sheet;
  if (section === 'setup' && !finiteAmount(plan.dev_week_rate)) plan.dev_week_rate = rateFor(plan.dev_level, rules);
  const monthly = section === 'monthly' ? snapshotMonthlyRates(sheet.monthly, rules) : sheet.monthly;
  const next = invalidatePricingSections({ ...sheet, monthly, plan }, section);
  return { ...next, plan: { ...next.plan!, section_confirmations: {
    ...next.plan?.section_confirmations, [section]: pricingSectionBasis(next, section),
  } } };
}

/** Freeze the effective usage rates in a new project decision so later
 * methodology edits cannot silently change a submitted monthly contract. */
export function snapshotMonthlyRates(rows: PricingMonthlyRow[], rules: PricingRules = DEFAULT_RULES): PricingMonthlyRow[] {
  return rows.map(row => {
    if (row.model !== 'usage') return row;
    const unit = row.unit && row.unit in UNIT_WORDS ? row.unit : 'custom';
    return { ...row, unit,
      rate: finiteAmount(row.rate) ? row.rate : unit === 'custom' ? null : rules.unit_rates[unit],
      minimum: finiteAmount(row.minimum) ? Math.floor(row.minimum) : rules.unit_minimums[unit],
    };
  });
}

/** Keep only checks that still describe this exact draft. */
export function normalizePricingConfirmations(sheet: PricingSheet): PricingSheet {
  if (sheet.plan?.workflow_version !== 'lato-pricing-v2') return sheet;
  if (casePricingEnabled(sheet.plan)) sheet = { ...sheet, plan: { ...sheet.plan, case_pricing: sheet.plan.case_pricing!.map(decision => {
    const section_confirmations: Partial<Record<PricingCaseSection, string>> = {};
    for (const section of ['development', 'setup'] as const) {
      if (casePricingSectionConfirmed(sheet, decision.case, section)) section_confirmations[section] = decision.section_confirmations![section];
    }
    return { ...decision, section_confirmations };
  }) } };
  const confirmations: Partial<Record<PricingSection, string>> = {};
  for (const section of PRICING_SECTIONS) {
    if (pricingSectionConfirmed(sheet, section)) confirmations[section] = sheet.plan!.section_confirmations![section];
  }
  return { ...sheet, plan: { ...sheet.plan!, section_confirmations: confirmations } };
}

export interface MonthlyLines {
  minimum_monthly: number | null;
  price: string | null;
  overage: string | null;
  sentence: string;
}

export function monthlyLines(row: PricingMonthlyRow, rules: PricingRules = DEFAULT_RULES): MonthlyLines {
  if (row.model === 'simple' && row.simple) return simpleMonthlyLines(row.simple);
  const model = row.model === 'usage' ? 'usage' : 'fixed';
  if (model === 'fixed') {
    const amount = typeof row.amount === 'number' && Number.isFinite(row.amount) && row.amount >= 0 ? row.amount : null;
    if (amount === null) return { minimum_monthly: null, price: null, overage: null, sentence: '' };
    return { minimum_monthly: round2(amount), price: `${usd(amount)} / month`, overage: null, sentence: `Fixed fee — ${usd(amount)} per month.` };
  }
  const unit: UsageUnit = row.unit && row.unit in UNIT_WORDS ? row.unit : 'custom';
  const rate = typeof row.rate === 'number' && Number.isFinite(row.rate) && row.rate >= 0
    ? row.rate
    : unit !== 'custom' ? rules.unit_rates[unit] : null;
  const minimum = typeof row.minimum === 'number' && Number.isFinite(row.minimum) && row.minimum >= 0
    ? row.minimum
    : rules.unit_minimums[unit];
  if (rate === null) return { minimum_monthly: null, price: null, overage: null, sentence: '' };
  const words = unit === 'custom'
    ? { one: (row.unit_label || 'unit').trim(), many: (row.unit_label || 'units').trim(), note: '' }
    : UNIT_WORDS[unit];
  const minMonthly = round2(rate * minimum);
  const per = `per ${words.one}`;
  const price = minimum > 0
    ? `${usd(minMonthly)} / month (includes ${minimum} ${minimum === 1 ? words.one : words.many})`
    : `${usd(rate)} ${per}`;
  const overage = `${usd(rate)} per additional ${words.one}`;
  const sentence = minimum > 0
    ? `${usd(rate)} ${per}${words.note ? ` (${words.note})` : ''} · minimum ${minimum} ${minimum === 1 ? words.one : words.many} — ${usd(minMonthly)} per month includes ${minimum} ${minimum === 1 ? words.one : words.many}; each additional ${words.one} is ${usd(rate)}.`
    : `${usd(rate)} ${per}${words.note ? ` (${words.note})` : ''}, billed on usage.`;
  return { minimum_monthly: minMonthly, price, overage, sentence };
}

export function monthlyMinimumTotal(rows: PricingMonthlyRow[], rules: PricingRules = DEFAULT_RULES): number {
  return round2(rows.reduce((a, m) => {
    if (m.model !== 'fixed' && m.model !== 'usage' && m.model !== 'simple') return a + (parseMoney(m.price) ?? 0);
    return a + (monthlyLines(m, rules).minimum_monthly ?? 0);
  }, 0));
}

export function setupTotal(setup: PricingSetupRow[]): number {
  return round2(setup.reduce((a, r) => a + (parseMoney(r.price) ?? 0), 0));
}

/** A fresh monthly row carried from a case, model still to be chosen. */
export function monthlyRowFromCase(r: PricingSetupRow, unit?: UsageUnit | null, rules: PricingRules = DEFAULT_RULES): PricingMonthlyRow {
  if (unit) {
    return { label: r.case, description: r.blurb || '', case: r.case, model: 'usage', unit, rate: unit === 'custom' ? null : rules.unit_rates[unit], minimum: rules.unit_minimums[unit], amount: null, price: null, overage: null };
  }
  return { label: r.case, description: r.blurb || '', case: r.case, model: 'fixed', amount: null, price: null, overage: null };
}
